/* eslint-disable @typescript-eslint/no-inferrable-types */
import { Injectable } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { IBasePermissions, IFileInfo, IItem, IItemUpdateResult, PermissionKind, sp } from '@pnp/sp/presets/all';
import { SpHttpClient } from 'src/app/auth'
import { execute } from './utils';
import { IFetchOptions } from '@pnp/common';
import { environment } from 'src/environments/environment';
import { AemSummary, ExportData, LibraryItem, ListItem } from './models';
import { ListTitles } from './constants';
import { getFileNameWithOutExtension, getFileExtension } from '../services/utils'
import { Workbook } from 'exceljs';
import { AnalyticsService } from './analytics.service';
import { ChannelService } from './channel.service';

@Injectable({
  providedIn: 'root'
})
export class DocumentLibraryService {

  constructor(private http: SpHttpClient, private sanitizer: DomSanitizer, private analyticsService: AnalyticsService, private channelService: ChannelService) { }

  async getThumbnailSafeUrl(serverRelativeUrl: string): Promise<SafeUrl> {
    const embededDataHeaders = new Headers();
    embededDataHeaders.set('accept', 'application/json;odata=nometadata');

    const embededDataOptions: IFetchOptions = {
      method: 'GET',
      headers: embededDataHeaders
    };
    const encodedFileAbsoluteUrl = encodeURIComponent(environment.serverUrl + serverRelativeUrl);
    const embededDataUrl = `${environment.siteUrl}/_api/SP.Publishing.EmbedService/EmbedData?url='${encodedFileAbsoluteUrl}'&version=1`;

    const embededData = await execute(async () => {
      const embededResponse = await this.http.fetch(embededDataUrl, embededDataOptions);
      return embededResponse.json();
    });

    const imageOptions = { method: 'GET' };
    if ((embededData.ThumbnailUrl as string).includes('getpreview.ashx')) {
      let extension = getFileExtension(serverRelativeUrl);
      if (extension) extension = extension.toLowerCase();
      else return undefined;
      if (['svg', 'png', 'jpg', 'jpeg', 'jfif', 'webp', 'gif'].includes(extension)) {
        return await this.getSafeUrl(serverRelativeUrl, false)
      }
      else return undefined;
    }
    const imageBlob = await execute(async () => {
      const imageResponse = await this.http.fetch(embededData.ThumbnailUrl, imageOptions);

      return imageResponse.blob();
    })
    const thumbnailUrl = this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(imageBlob));
    return thumbnailUrl;
  }

  async getSafeUrl(url: string, isAbsoluteUrl: boolean): Promise<SafeUrl> {
    const file = isAbsoluteUrl ? sp.web.getFileByUrl(url) : sp.web.getFileByServerRelativeUrl(url);
    const blob = await file.getBlob();
    const safeUrl = this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(blob));
    return safeUrl;
  }

  async getFileItems(libraryTitle: string, includeThumbnailSafeUrl: boolean): Promise<LibraryItem[]> {
    const libraryListItemsPromise = execute<LibraryItem[]>(() => sp.web.lists.getByTitle(libraryTitle).items
      .select('*, FileRef, Author/Title, Editor/Title, EffectiveBasePermissions')
      .expand('Author, Editor')
      .orderBy("Modified", false)
      .get());

    if (includeThumbnailSafeUrl) {
      const libraryListItems = await libraryListItemsPromise;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const thumbnailPromises = libraryListItems.map(x => {
        return this.getThumbnailSafeUrl(x.FileRef).then(url => {
          x.ThumbnailSafeUrl = url;
        });
      });
      // await Promise.all(thumbnailPromises);
      return libraryListItems;
    }
    else {
      return libraryListItemsPromise;
    }
  }

  async getFileItemsById(libraryTitle: string, includeThumbnailSafeUrl: boolean, id: number): Promise<LibraryItem[]> {
    const filterId = `AccessTrend/ID eq ${id}`;
    const libraryListItemsPromise = execute<LibraryItem[]>(() => sp.web.lists.getByTitle(libraryTitle).items
      .select('*, FileRef, Author/Title, Editor/Title, EffectiveBasePermissions,AccessTrend/ID')
      .expand('Author, Editor, AccessTrend')
      .filter(filterId)
      .orderBy('SortOrder', true)
      .get());

    if (includeThumbnailSafeUrl) {
      const libraryListItems = await libraryListItemsPromise;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const thumbnailPromises = libraryListItems.map(x => {
        return this.getThumbnailSafeUrl(x.FileRef).then(url => {
          x.ThumbnailSafeUrl = url;
        });
      });
      // await Promise.all(thumbnailPromises);
      return libraryListItems;
    }
    else {
      return libraryListItemsPromise;
    }
  }

  async getFiles(
    folderRelativeUrl: string,
    includeAllItemFields: boolean, includeThumbnailSafeUrl: boolean
  ): Promise<IFileInfo[]> {

    let files = sp.web.getFolderByServerRelativeUrl(folderRelativeUrl).files;
    if (includeAllItemFields) {
      files = files.select('*, ListItemAllFields').expand('ListItemAllFields');
    }
    const fileInfosPromise = execute(() => files.get());

    if (includeThumbnailSafeUrl) {
      const fileInfos = await fileInfosPromise;
      fileInfos.map(x => {
        return this.getThumbnailSafeUrl(x.ServerRelativeUrl).then(url => {
          x.ThumbnailSafeUrl = url;
        });
      });
      return fileInfos;
    }
    else {
      return fileInfosPromise;
    }

  }
  async addDocumentBlob(libraryTitle: string, file: Blob, overWrite: boolean, documentProperties?: any, fileName?: string, folderName?: string): Promise<any> {
    const list = sp.web.lists.getByTitle(libraryTitle);
    const files = folderName ? list.rootFolder.folders.getByName(folderName).files : list.rootFolder.files;
    // const files = folderName ? sp.web.getFolderByServerRelativeUrl(`${libraryTitle}/${folderName}`).files : list.rootFolder.files;
    const result = await execute(() => files.add(fileName, file, overWrite));
    if (documentProperties) {
      const fileInfo = await execute(() => sp.web.getFileByServerRelativeUrl(result.data.ServerRelativeUrl).select('*, ListItemAllFields').expand('ListItemAllFields').get());
      if (fileInfo && fileInfo.ServerRelativeUrl && fileInfo.ServerRelativeUrl.length > 0)
        this.analyticsService.trackUploadAnalyticsInfo(`${environment.serverUrl}${fileInfo.ServerRelativeUrl}`, documentProperties.ResourceType ? documentProperties.ResourceType : documentProperties.ResourceTypeId ? documentProperties.ResourceTypeId.toString() : '');
      await execute(() => list.items.getById(fileInfo.ListItemAllFields.Id).update(documentProperties, "*"));
      Object.assign(fileInfo.ListItemAllFields, documentProperties);
      return fileInfo;
    }
  }
  //libraryTitle: 'AC-HTA-Documents', folderName: '3691' (countryId)
  //libraryTitle: 'ACGuidance'
  async addDocument(libraryTitle: string, file: File, overWrite: boolean, documentProperties?: any, fileName?: string, folderName?: string): Promise<any> {
    const list = sp.web.lists.getByTitle(libraryTitle);
    const files = folderName ? list.rootFolder.folders.getByName(folderName).files : list.rootFolder.files;
    // const files = folderName ? sp.web.getFolderByServerRelativeUrl(`${libraryTitle}/${folderName}`).files : list.rootFolder.files;
    const result = await execute(() => files.add(fileName || file.name, file, overWrite));
    if (documentProperties) {
      const fileInfo = await execute(() => sp.web.getFileByServerRelativeUrl(result.data.ServerRelativeUrl).select('*, ListItemAllFields').expand('ListItemAllFields').get());
      if (fileInfo && fileInfo.ServerRelativeUrl && fileInfo.ServerRelativeUrl.length > 0)
        this.analyticsService.trackUploadAnalyticsInfo(`${environment.serverUrl}${fileInfo.ServerRelativeUrl}`, documentProperties.ResourceType ? documentProperties.ResourceType : documentProperties.ResourceTypeId ? documentProperties.ResourceTypeId.toString() : '');
      await execute(() => list.items.getById(fileInfo.ListItemAllFields.Id).update(documentProperties, "*"));
      Object.assign(fileInfo.ListItemAllFields, documentProperties);
      return fileInfo;
    }
  }
  async addDocumentWithId(libraryTitle: string, file: File, overWrite: boolean, documentProperties?: any, fileName?: string, folderName?: string): Promise<any> {
    const list = sp.web.lists.getByTitle(libraryTitle);
    const files = folderName ? list.rootFolder.folders.getByName(folderName).files : list.rootFolder.files;
    // const files = folderName ? sp.web.getFolderByServerRelativeUrl(`${libraryTitle}/${folderName}`).files : list.rootFolder.files;
    const result = await execute(() => files.add(fileName || file.name, file, overWrite));
    if (documentProperties) {
      const fileInfo = await execute(() => sp.web.getFileByServerRelativeUrl(result.data.ServerRelativeUrl).select('*, ListItemAllFields').expand('ListItemAllFields').get());
      documentProperties['docId'] = fileInfo.ListItemAllFields.Id;
      if (fileInfo && fileInfo.ServerRelativeUrl && fileInfo.ServerRelativeUrl.length > 0)
        this.analyticsService.trackUploadAnalyticsInfo(`${environment.serverUrl}${fileInfo.ServerRelativeUrl}`, documentProperties.ResourceType ? documentProperties.ResourceType : documentProperties.ResourceTypeId ? documentProperties.ResourceTypeId.toString() : '');
      await execute(() => list.items.getById(fileInfo.ListItemAllFields.Id).update(documentProperties, "*"));
      Object.assign(fileInfo.ListItemAllFields, documentProperties);
      return fileInfo;
    }
  }


  //AccessTrends Document upload with ID
  async addTrendsDocumentWithId(sortOrder: number, accessId: number, libraryTitle: string, file: File, overWrite: boolean, documentProperties?: any, fileName?: string, folderName?: string): Promise<any> {
    const list = sp.web.lists.getByTitle(libraryTitle);
    const files = folderName ? list.rootFolder.folders.getByName(folderName).files : list.rootFolder.files;
    // const files = folderName ? sp.web.getFolderByServerRelativeUrl(`${libraryTitle}/${folderName}`).files : list.rootFolder.files;
    const result = await execute(() => files.add(fileName || file.name, file, overWrite));
    if (documentProperties) {
      const fileInfo = await execute(() => sp.web.getFileByServerRelativeUrl(result.data.ServerRelativeUrl)
        .select('*, ListItemAllFields').expand('ListItemAllFields').get()
      );

      //documentProperties['docId'] = fileInfo.ListItemAllFields.Id;
      fileInfo.ListItemAllFields['SortOrder'] = Number(sortOrder);
      fileInfo.ListItemAllFields['AccessTrendId'] = Number(accessId);
      if (fileInfo && fileInfo.ServerRelativeUrl && fileInfo.ServerRelativeUrl.length > 0)
        this.analyticsService.trackUploadAnalyticsInfo(`${environment.serverUrl}${fileInfo.ServerRelativeUrl}`, documentProperties.ResourceType ? documentProperties.ResourceType : documentProperties.ResourceTypeId ? documentProperties.ResourceTypeId.toString() : '');
      await execute(() => list.items.getById(fileInfo.ListItemAllFields.Id).update(documentProperties, "*"));
      await execute(() => list.items.getById(fileInfo.ListItemAllFields.Id)
        .update({
          SortOrder: Number(sortOrder),
          AccessTrendId: Number(accessId)

        }));
      Object.assign(fileInfo.ListItemAllFields, documentProperties);

      return fileInfo;
    }
  }
  
  async addContractRightDocument(taid:number, assetid:number, libraryTitle: string, file: File, overWrite: boolean, documentProperties?: any, fileName?: string, folderName?: string): Promise<any> {
    const list = sp.web.lists.getByTitle(libraryTitle);
    const files = folderName ? list.rootFolder.folders.getByName(folderName).files : list.rootFolder.files;
    // const files = folderName ? sp.web.getFolderByServerRelativeUrl(`${libraryTitle}/${folderName}`).files : list.rootFolder.files;
    const result = await execute(() => files.add(fileName || file.name, file, overWrite));
    if (documentProperties) {
      const fileInfo = await execute(() => sp.web.getFileByServerRelativeUrl(result.data.ServerRelativeUrl)
        .select('*, ListItemAllFields').expand('ListItemAllFields').get()
      );

      console.log(fileInfo);
      //documentProperties['docId'] = fileInfo.ListItemAllFields.Id;
      
      fileInfo.ListItemAllFields['TAId'] = Number(taid);
      fileInfo.ListItemAllFields['AssetId'] = Number(assetid);
      if (fileInfo && fileInfo.ServerRelativeUrl && fileInfo.ServerRelativeUrl.length > 0)
        this.analyticsService.trackUploadAnalyticsInfo(`${environment.serverUrl}${fileInfo.ServerRelativeUrl}`, documentProperties.ResourceType ? documentProperties.ResourceType : documentProperties.ResourceTypeId ? documentProperties.ResourceTypeId.toString() : '');
      await execute(() => list.items.getById(fileInfo.ListItemAllFields.Id).update(documentProperties, "*"));
      await execute(() => list.items.getById(fileInfo.ListItemAllFields.Id)
        .update({
          TAId: Number(taid),
          AssetId: Number(assetid)

        }));
      Object.assign(fileInfo.ListItemAllFields, documentProperties);

      return fileInfo;
    }
  }


  //libraryTitle: 'AC-HTA-Documents', folderName: '3691' (countryId)
  //libraryTitle: 'ACGuidance'
  async addDocumentOnFolder(libraryTitle: string, file: File, overWrite: boolean, documentProperties?: any, fileName?: string, folderName?: string): Promise<any> {
    const list = sp.web.lists.getByTitle(libraryTitle);
    const files = folderName ? sp.web.getFolderByServerRelativeUrl(`${libraryTitle}/${folderName}`).files : list.rootFolder.files;
    const result = await execute(() => files.add(fileName || file.name, file, overWrite));
    if (documentProperties) {
      const fileInfo = await execute(() => sp.web.getFileByServerRelativeUrl(result.data.ServerRelativeUrl).select('*, ListItemAllFields').expand('ListItemAllFields').get());
      if (fileInfo && fileInfo.ServerRelativeUrl && fileInfo.ServerRelativeUrl.length > 0)
        this.analyticsService.trackUploadAnalyticsInfo(`${environment.serverUrl}${fileInfo.ServerRelativeUrl}`, documentProperties.ResourceType ? documentProperties.ResourceType : documentProperties.ResourceTypeId ? documentProperties.ResourceTypeId.toString() : '');
      await execute(() => list.items.getById(fileInfo.ListItemAllFields.Id).update(documentProperties, "*"));
      Object.assign(fileInfo.ListItemAllFields, documentProperties);
      return fileInfo;
    }
  }
  

  async addFolder(libraryTitle: string, folderRelativePath: string, folderProperties?: any): Promise<any> {
    const list = sp.web.lists.getByTitle(libraryTitle);

    const result = await execute(() => sp.web.folders.add(folderRelativePath));
    if (folderProperties) {
      const folderInfo = await execute(() => sp.web.getFolderByServerRelativeUrl(result.data.ServerRelativeUrl).select('*, ListItemAllFields').expand('ListItemAllFields').get());
      await execute(() => list.items.getById(folderInfo.ListItemAllFields.Id).update(folderProperties, "*"));
      Object.assign(folderInfo.ListItemAllFields, folderProperties);
      return folderInfo;
    }
    else {
      return result;
    }
  }

  async updateDocumentData(listTitle: string, itemId: number, documentProperties?: any): Promise<void> {
    if (documentProperties["SharedWithUsersId"] == null || documentProperties["SharedWithUsersId"] === undefined)
      delete documentProperties["SharedWithUsersId"];

    await execute(() => sp.web.lists.getByTitle(listTitle).items.getById(itemId).update(documentProperties, "*"));
  }

  async updateHtaDocumentsComment(listTitle: string, docs: any): Promise<void> {
    const batch = sp.web.createBatch();
    docs.forEach(doc => {
      const docProperties = {
        Comments: doc.Comments
      };
      sp.web.lists.getByTitle(listTitle).items.getById(doc.Id).inBatch(batch).update(docProperties, "*");
    });
    await batch.execute();
  }

  async deleteItem(listTitle: string, item: ListItem): Promise<void> {
    await execute(() => sp.web.lists.getByTitle(listTitle).items.getById(item.Id).recycle());
  }

  async deleteFileByUrl(url: string): Promise<void> {
    await execute(() => sp.web.getFileByServerRelativeUrl(url).recycle());
  }

  async deleteResourceFile(listTitle: string, item: any): Promise<void> {
    const id = item && item.Id && item.Id !== undefined ? item.Id : item.ListItemAllFields.Id;
    await execute(() => sp.web.lists.getByTitle(listTitle).items.getById(id).recycle());
  }
  async deleteAemSummaryFiles(listTitle: string, items: AemSummary[]): Promise<void> {
    if (items && items.length > 1) {
      const batch = sp.web.createBatch();
      items.forEach(x => {
        if (x.Id) {
          sp.web.lists.getByTitle(listTitle).items.getById(x.Id).inBatch(batch).recycle();
        }
        else {
          sp.web.getFileByServerRelativeUrl(x.FileRef).inBatch(batch).recycle();
        }
      });
      await batch.execute();
    }
    else {
      await execute(() => sp.web.lists.getByTitle(listTitle).items.getById(items[0].Id).recycle());
    }
  }
  async deleteMaapEvidenceTrackerFiles(listTitle: string, items: AemSummary[]): Promise<void> {
    if (items && items.length > 1) {
      const batch = sp.web.createBatch();
      items.forEach(x => {
        if (x.Id) {
          sp.web.lists.getByTitle(listTitle).items.getById(x.Id).inBatch(batch).recycle();
        }
        else {
          sp.web.getFileByServerRelativeUrl(x.FileRef).inBatch(batch).recycle();
        }
      });
      await batch.execute();
    }
    else {
      await execute(() => sp.web.lists.getByTitle(listTitle).items.getById(items[0].Id).recycle());
    }
  }

  async addHtaKeywords(keywords: string[]): Promise<void> {
    const filter = keywords.map(k => `Title eq '${k}'`).join(' or ')
    const list = sp.web.lists.getByTitle(ListTitles.HtaKeywords);
    const existingKeyworkdsItems = await execute<ListItem[]>(() => list.items.select('Id, Title').filter(filter).get());
    const keywordsToAdd = keywords.filter(k =>
      existingKeyworkdsItems.findIndex(x => x.Title.toLowerCase() === k.toLowerCase()) === -1);

    const batch = sp.web.createBatch();
    keywordsToAdd.forEach(keyword => {
      const keywordItem = {
        Title: keyword,
        isVerifiedKeyWord: false
      };
      list.items.inBatch(batch).add(keywordItem);
    });

    await batch.execute();
  }

  generateLinkFile(url: string, prefixCode: boolean = false, sufixCode: boolean = false): File {
    let fileContent = '[InternetShortcut]\nURL=';

    const idForName = new Date();
    let fileName = prefixCode ? `LINK${this.createFileNameCode()}${idForName.getMinutes().toLocaleString()}${idForName.getSeconds().toLocaleString()}` : '';

    fileName += this.getURLDomainName(url);
    fileName += sufixCode ? `${this.createFileNameCode()}.url` : '.url';
    fileContent += url;

    const blob = new Blob([fileContent], { type: '.url' });
    const file = new File([blob], fileName);

    return file;
  }

  createFileNameCode(): string {
    const code = Math.floor(Math.random() * (1257));
    return '_' + code;
  }

  getURLDomainName(url: string): string {
    const _domain = url.match(/^(?:https?:\/\/)?(?:[^@/\n]+@)?(?:www\.)?([^:/?\n]+)/igm);
    const domain = _domain[0].match(/(?:[^@/\n]+@)?(?:www\.)?([^:/?\n]+)/igm);
    return domain[domain.length - 1];
  }

  async getFileBlob(fileUrl: string): Promise<Blob> {
    return sp.web.getFileByServerRelativeUrl(fileUrl).getBlob();
  }
  async getFileBase64(id, isAcHtaFaqValue: boolean) {
    return await sp.web.lists.getByTitle(isAcHtaFaqValue ? ListTitles.acHtaFAQAnswersDocuments : ListTitles.FAQAnswersDocuments).items.select("Id", "QAId", "FAQbase64").filter(`QAId eq '${id}'`).get();//Item("QAId", "FAQbase64");


  }
  //<!-- Export Side bar code -->
  async createNewData(exportItem: ExportData, tracker1, tracker2) {
    await sp.web.lists.getByTitle('MAAT Evidence Tracker Export Request').items.add({
      Name: exportItem.Name,
      Email: exportItem.Email,
      Department: exportItem.Department,
      BusinessReason: exportItem.BusinessReason
    }).then((itemCreated) => {
      console.log("Done adding", itemCreated);
      this.exportData(itemCreated, tracker1, tracker2);
    })
    //let exportList = await this.getExpertRequests();
    //this.exportAdviceRequest(exportList);
  }

  exportData(itemCreated, headerValues, exportTableData): void {
    let detailsToAdd = [];			
    let itemsforBatch = [];

    //let array = [{name:"a",place:"ooty"},{name:"b",place:"chennai"}];   
    const excelDataValues = exportTableData;
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('ExportData');
    const ResultRowsAdviceRequest = { columns: [], data: [] };
    //const ConstantTitleColumns = ["File", "TableName", "Endpoint", "Study", "Subgroup", "Method", "Timepoint"];      
    const ConstantTitleColumns = headerValues;
    ConstantTitleColumns.forEach(element => {
      const newRow = { header: element.title };
      ResultRowsAdviceRequest["columns"].push(newRow);
    });
    worksheet.columns = ResultRowsAdviceRequest.columns;
    worksheet.addRows(ResultRowsAdviceRequest.data);
    excelDataValues.map((excelArray) => {
      const tempAdviceRequest = [];
      //excelArray.shift();       
      excelArray.map((eachitem) => {
        tempAdviceRequest.push(eachitem);
      });
      ResultRowsAdviceRequest.data.push(tempAdviceRequest);
      //tempAdviceRequest.push(advideRequest.place);      
    });
    worksheet.addRows(ResultRowsAdviceRequest.data);
    worksheet.columns.forEach(function (column) {
      column.width = 25;
    });
    worksheet.getRows(1, 1).forEach((row) => {
      row.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'ccddf0' }
      };
    });
    workbook.xlsx.writeBuffer().then((data) => {
      const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      //saveAs(blob, 'ExportDataExcel.xlsx');
      const item: IItem = sp.web.lists.getByTitle("MAAT Evidence Tracker Export Request").items.getById(itemCreated.data.ID);
      // add an attachment
      item.attachmentFiles.add('ExportDataExcel.xlsx', blob);

      // NewWF - Start		
      detailsToAdd.push("MAAP_SendEmailToMAAPAdmin");     			
      detailsToAdd.push(itemCreated.data.ID.toString());    			
      itemsforBatch.push(detailsToAdd);			
      this.channelService.createNewPowerAutomateTrigger(itemsforBatch);			
      // NewWF - End
    });
  }
  //

  async getCSVFile() {
    const url = `https://devlabs365.sharepoint.com/sites/AccessHub/AEM%20Guidance/14000_UPAAD_TABLEDATA_data.csv`;
    // const url = `${environment.siteUrl}/MAAT%20Evidence%20Tracker%20Files/`+title;
    return sp.web.getFileByUrl(url).getText();
  }
  async getCSVFileDynamic(title: string) {
    const url = `${environment.siteUrl}/MAAT%20Evidence%20Tracker%20Files/` + title;
    return sp.web.getFileByUrl(url).getText();
  }


  async generateNewFileName(ListTitle: string, file: File, countryId?: number): Promise<string> {
    const sameFileAsNew = countryId ? await execute(() => sp.web.getFolderByServerRelativePath(`${ListTitle}/${countryId}`).files.filter(`Title eq '${file.name}'`).get())
      : await sp.web.lists.getByTitle(ListTitle).items.filter(`Title eq '${file.name}'`).get();
    let newFileName = '';
    if (sameFileAsNew && sameFileAsNew.length > 0) {
      const fileNameWithOutExtension = getFileNameWithOutExtension(file.name);
      const fileExtension = getFileExtension(file.name);
      const aemSummaryFiles = countryId ? await execute(() => sp.web.getFolderByServerRelativePath(`${ListTitle}/${countryId}`).files.filter(`startswith(Title,'${fileNameWithOutExtension}')`).get())
        : await execute(() => sp.web.lists.getByTitle(ListTitle).items.filter(`startswith(Title,'${fileNameWithOutExtension}')`).get());
      let auxAemSummaryFiles = [...aemSummaryFiles];
      auxAemSummaryFiles.forEach(x => {
        if (getFileExtension(x.Title) !== fileExtension) {
          aemSummaryFiles.splice(aemSummaryFiles.indexOf(x), 1);
        }
      });

      if (aemSummaryFiles && aemSummaryFiles.length > 0) {
        const nameArray = fileNameWithOutExtension.split(".")
        if (nameArray && nameArray.length > 0) {
          nameArray.forEach(x => {
            if (newFileName == '') newFileName += x;
            else newFileName = newFileName + "." + x;
          });
        }
        else {
          newFileName = fileNameWithOutExtension;
        }

        const auxFileName = newFileName + ` (${aemSummaryFiles.length})`;
        auxAemSummaryFiles = [...aemSummaryFiles];
        const controlFinalName = auxAemSummaryFiles.find(x => getFileNameWithOutExtension(x.Title) === auxFileName);
        if (controlFinalName && controlFinalName !== undefined) {
          let maxNumberForFileName = 0;
          auxAemSummaryFiles.forEach(x => {
            const countArray = getFileNameWithOutExtension(x.Title).split('(');
            const value = parseInt(countArray[countArray.length - 1].split(')')[0]);
            if (value > maxNumberForFileName) {
              maxNumberForFileName = value;
            }
          });
          newFileName += ` (${maxNumberForFileName + 1})`;
        }
        else {
          newFileName += ` (${aemSummaryFiles.length})`;
        }
        newFileName = newFileName + "." + fileExtension;
      }
      else {
        newFileName = file.name;
      }
    }
    else {
      newFileName = file.name;
    }
    return newFileName;
  }

  getCurrentUserEffectivePermissions(listTitle: string): Promise<IBasePermissions> {
    return execute(() => sp.web.lists.getByTitle(listTitle).getCurrentUserEffectivePermissions());
  }

  currentUserHasPermission(listTitle: string, permission: PermissionKind): Promise<boolean> {
    return execute(() => sp.web.lists.getByTitle(listTitle).currentUserHasPermissions(permission));
  }

  getCurrentUserListItemEffectivePermissions(listTitle: string, listItemId: number): Promise<IBasePermissions> {
    return execute(() => sp.web.lists.getByTitle(listTitle).items.getById(listItemId).getCurrentUserEffectivePermissions());
  }

  currentUserHasPermissionOverListItem(listTitle: string, listItemId: number, permission: PermissionKind): Promise<boolean> {
    return execute(() => sp.web.lists.getByTitle(listTitle).items.getById(listItemId).currentUserHasPermissions(permission));
  }

  renameFolder(folderRelativeUrl: string, newFolderName: string): Promise<IItemUpdateResult> {
    return execute(async () => {
      const folderItem = await sp.web.getFolderByServerRelativeUrl(folderRelativeUrl).getItem();

      return folderItem.update({ FileLeafRef: newFolderName });
    });
  }

  renameFile(fileRelativeUrl: string, newFileName: string): Promise<void> {
    const fileUrlSplit = fileRelativeUrl.split('/');
    const fileFolder = fileUrlSplit.slice(0, fileUrlSplit.length - 1).join('/');
    const fileExtension = getFileExtension(fileRelativeUrl);

    return execute(async () => {
      const folderItem = await sp.web.getFileByServerRelativeUrl(fileRelativeUrl);

      return folderItem.moveTo(`${fileFolder}/${newFileName}.${fileExtension}`);
    });
  }

  async getPastHtaFileItems(): Promise<any> {
    const items = await sp.web.lists.getByTitle(ListTitles.MarketAccessDatabases).items
      .orderBy('SortOrder', true)
      .get();
    return items;
  }
  async getPastHTAById(id): Promise<any> {
    const pastHTAitem = await sp.web.lists.getByTitle(ListTitles.MarketAccessDatabases).items.getById(id)();
    return pastHTAitem;
  }
  async getPastHtaButtondetails(id): Promise<any> {
    const filterId = `DataBaseName/ID eq ${id}`;
    const details = await sp.web.lists.getByTitle(ListTitles.MarketAccessDatabases_Buttons).items
      .select('DataBaseName/ID,ButtonTitle,ButtonType,ButtonDescription,ButtonIcon,NavigationURL,ButtonClass')
      .expand('DataBaseName')
      .filter(filterId)
      .getAll();
    return details;
  }
  async getKeyFeatures(id): Promise<any> {
    const keyId = `DataBaseName/ID eq ${id}`;
    const keyList = await sp.web.lists.getByTitle(ListTitles.MarketAccessDatabases_KeyFeatures).items
      .select('DataBaseName/ID,KeyFeature,SortOrder,Column')
      .expand('DataBaseName')
      .orderBy("SortOrder", true)
      .filter(keyId)
      .getAll();
    return keyList;
  }

  async getAercCatalogFileItems(libraryTitle: string, includeThumbnailSafeUrl: boolean): Promise<LibraryItem[]> {
    const libraryListItemsPromise = execute<LibraryItem[]>(() => sp.web.lists.getByTitle(libraryTitle).items
      .select('*, FileRef, Author/Title, Editor/Title, EffectiveBasePermissions,SortOrder')
      .expand('Author, Editor')
      .orderBy("SortOrder", true)
      .get());

    if (includeThumbnailSafeUrl) {
      const libraryListItems = await libraryListItemsPromise;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const thumbnailPromises = libraryListItems.map(x => {
        return this.getThumbnailSafeUrl(x.FileRef).then(url => {
          x.ThumbnailSafeUrl = url;
        });
      });
      // await Promise.all(thumbnailPromises);
      return libraryListItems;
    }
    else {
      return libraryListItemsPromise;
    }
  }
  
}
